import { useEffect, useMemo, useState } from "react";
import { Dialog } from "primereact/dialog";
import Button from "../../../componentes/button/button";
import { retornaTipoDaRota } from "../../../utils/options";
import CargaEntregaService from "../../../services/resource/escalaDeRotaService";
import { capitalizeNome } from "../../../utils/reduzirNome";
import { formatLocalDate, formatLocalDateTime } from "../../../utils/formatDate";
import ResumoRota from "../ResumoRota";
import { toast } from "react-toastify";
import { useAuth } from "../../../context/authContext";
import TabelaRetornosDeEntregas from "../escala-de-rotas/TabelaRetornosDeEntregas";
import { useSearchParams } from "react-router-dom";

const fmtN = (v) => (v != null ? Number(v).toLocaleString("pt-BR") : "—");
const fmtP = (v) => (v != null ? `${Number(v).toFixed(2)}%` : "—");

function TabelaCargaEntregaPhone() {
    const [searchParams] = useSearchParams();

    const ano = Number(searchParams.get("ano"));
    const mes = Number(searchParams.get("mes"));
    const filialId = Number(searchParams.get("filialId"));
    const funcionarioId = searchParams.get("funcionarioId");

    const { user } = useAuth();
    const roleAdmin = user.perfis.includes("ADMIN");
    const cargaService = new CargaEntregaService();

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedCarga, setSelectedCarga] = useState(null);
    const [modalRetornos, setModalRetornos] = useState(false);
    const [modalResumo, setModalResumo] = useState(false);
    const [search, setSearch] = useState("");

    useEffect(() => {
        if (ano && mes) buscarCargas();
    }, [ano, mes, funcionarioId, filialId]);

    const buscarCargas = () => {
        setLoading(true);
        cargaService
            .listarPorPeriodo(funcionarioId, ano, mes, filialId)
            .then((res) => setData(res.data ?? []))
            .finally(() => setLoading(false));
    };

    const filtrados = useMemo(() => {
        if (!search.trim()) return data;
        const q = search.toLowerCase();
        return data.filter(
            (r) =>
                (r.carga ?? "").toLowerCase().includes(q) ||
                (r.transporte ?? "").toLowerCase().includes(q) ||
                (r.motoristaNome ?? "").toLowerCase().includes(q) ||
                (r.filialNome ?? "").toLowerCase().includes(q)
        );
    }, [data, search]);

    const totais = useMemo(
        () =>
            filtrados.reduce(
                (acc, r) => ({
                    entregas: acc.entregas + (Number(r.entregaVolumeTotalCliente) || 0),
                    cubos: acc.cubos + (Number(r.entregaVolumeCubos) || 0),
                }),
                { entregas: 0, cubos: 0 }
            ),
        [filtrados]
    );

    const excluir = () => {
        if (!window.confirm("Confirma o cancelamento desta carga?")) return;
        cargaService.excluirCarga(selectedCarga.id).then(() => {
            setData((prev) => prev.filter((c) => c.id !== selectedCarga.id));
            toast.success("Carga deletada com sucesso");
            setModalResumo(false);
            setSelectedCarga(null);
        });
    };

    return (
        <div className="min-h-screen flex flex-col h-full">

            {/* ── Barra de busca ── */}
            <div className="flex items-center gap-2 px-3 py-2 border-b bg-white sticky top-0 z-10">
                <div className="relative flex-1">
                    <i className="fa fa-search absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs pointer-events-none" />
                    <input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Buscar carga, motorista, filial..."
                        className="w-full pl-7 pr-3 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                    />
                </div>
            </div>

            {/* ── Lista de cards ── */}
            <div className="flex-1 overflow-y-auto px-3 py-3 space-y-2 bg-gray-50">
                {loading ? (
                    <LoadingState />
                ) : filtrados.length === 0 ? (
                    <EmptyState />
                ) : (
                    filtrados.map((row) => (
                        <CargaCard
                            key={row.id}
                            row={row}
                            onClick={() => { setSelectedCarga(row); setModalResumo(true); }}
                            onRetornos={() => {
                                setSelectedCarga({ filialId: row.filialId, transporte: row.transporte });
                                setModalRetornos(true);
                            }}
                        />
                    ))
                )}
            </div>

            {/* ── Rodapé totais ── */}
            {filtrados.length > 0 && !loading && (
                <div className="border-t bg-white px-3 py-2">
                    <p className="text-[10px] font-semibold text-gray-500 uppercase mb-1.5">
                        Totais — {filtrados.length} carga{filtrados.length !== 1 ? "s" : ""}
                    </p>
                    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                        {[
                            { label: "Cargas", value: filtrados.length },
                            { label: "Clientes", value: fmtN(totais.entregas) },
                            { label: "Cubagem", value: fmtN(totais.cubos) },
                        ].map((t) => (
                            <div key={t.label} className="text-center bg-gray-50 rounded-lg py-1.5 px-1">
                                <p className="text-[9px] text-gray-400 uppercase">{t.label}</p>
                                <p className="text-xs font-bold text-gray-800">{t.value}</p>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* ── Modal retornos ── */}
            <Dialog
                visible={modalRetornos}
                onHide={() => setModalRetornos(false)}
                className="w-[98%] max-w-3xl"
                blockScroll
                contentClassName="p-0"
                footer={
                    <div className="flex justify-end pt-3">
                        <Button variant="gray" label="Voltar" iconLeft="fa fa-arrow-left" onClick={() => setModalRetornos(false)} />
                    </div>
                }
            >
                <TabelaRetornosDeEntregas values={selectedCarga} />
            </Dialog>

            {/* ── Modal resumo da rota ── */}
            <Dialog
                visible={modalResumo}
                onHide={() => { setModalResumo(false); setSelectedCarga(null); }}
                className="w-[98%] max-w-3xl"
                blockScroll
                contentClassName="p-0"
                headerClassName="p-4 pb-3"
                header={
                    selectedCarga && (
                        <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap mb-2">
                                    <span className="text-lg leading-none">🚚</span>
                                    <span className="font-extrabold text-sm tracking-tight">
                                        Rota #{selectedCarga.id} — {selectedCarga.carga}
                                    </span>
                                </div>
                                <div className="flex flex-wrap gap-1.5">
                                    <Badge label={selectedCarga.filialNome} color="blue" />
                                    <Badge label={retornaTipoDaRota(selectedCarga.tipoRota)} color="teal" />
                                    <Badge label={selectedCarga.rotaGreenmileEntregueNoPrazo} color="green" />
                                    {selectedCarga.rotaGreenmileImagens
                                        ? <Badge label="✔ Imagens" color="violet" />
                                        : <Badge label="✘ Sem imagens" color="red" />
                                    }
                                </div>
                            </div>
                            <div className="text-right flex-shrink-0">
                                <p className="text-[10px] text-gray-400">Operação</p>
                                <p className="font-bold text-sm">{formatLocalDate(selectedCarga.dataOperacao)}</p>
                                <p className="text-[10px] text-gray-400 mt-0.5">
                                    {formatLocalDateTime(selectedCarga.dataCriacao)}
                                </p>
                            </div>
                        </div>
                    )
                }
                footer={
                    <div className="flex justify-between pt-3">
                        <div>
                            {roleAdmin && (
                                <Button label="Excluir" variant="white" iconLeft="fa fa-times" onClick={excluir} />
                            )}
                        </div>
                        <Button
                            variant="gray" label="Voltar" iconLeft="fa fa-arrow-left"
                            onClick={() => { setModalResumo(false); setSelectedCarga(null); }}
                        />
                    </div>
                }
            >
                {selectedCarga && <ResumoRota rota={selectedCarga} />}
            </Dialog>
        </div>
    );
}

export default TabelaCargaEntregaPhone;

// ─── Card de carga (mobile-first) ────────────────────────────────────────────
function CargaCard({ row, onClick, onRetornos }) {
    const [open, setOpen] = useState(false);
    const noPrazo = row.rotaGreenmileEntregueNoPrazo;

    return (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">

            {/* Linha principal */}
            <div
                className="flex items-start justify-between gap-3 px-4 py-3 cursor-pointer"
                onClick={() => setOpen((v) => !v)}
            >
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-bold text-sm text-gray-800">{row.carga}</span>
                        {noPrazo && (
                            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-green-100 text-green-700">
                                {noPrazo}
                            </span>
                        )}
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5 truncate">
                        {capitalizeNome(row.motoristaNome) ?? "—"}
                        {row.filialNome && <span className="ml-2 text-gray-400">· {row.filialNome}</span>}
                    </p>
                    <p className="text-[10px] text-gray-400 mt-0.5">
                        {retornaTipoDaRota(row.tipoRota)}
                        {row.veiculoPlaca && <span className="ml-2">· {row.veiculoPlaca}</span>}
                    </p>
                </div>
                <div className="text-right flex-shrink-0">
                    <p className="text-[10px] text-gray-400">Data</p>
                    <p className="text-xs font-medium text-gray-700">{formatLocalDate(row.dataOperacao) ?? "—"}</p>
                    <i className={`fa fa-chevron-down text-gray-400 text-[10px] mt-1 transition-transform block ${open ? "rotate-180" : ""}`} />
                </div>
            </div>

            {/* Detalhes expandidos */}
            {open && (
                <div className="border-t border-gray-100 px-4 py-3 space-y-3 bg-gray-50/60">

                    {/* Equipe */}
                    <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                        <Detail label="Auxiliar 1" value={capitalizeNome(row.auxiliar1Nome)} />
                        <Detail label="Auxiliar 2" value={capitalizeNome(row.auxiliar2Nome)} />
                        <Detail label="Transporte" value={row.transporte} />
                        <Detail label="% Ocupação" value={fmtP(row.ocupacao)} />
                    </div>

                    {/* Cubagem e clientes */}
                    <div className="grid grid-cols-3 gap-2">
                        {[
                            { label: "Cubos Esc.", value: fmtN(row.cubos) },
                            { label: "Cubos SAP", value: fmtN(row.entregaVolumeCubos) },
                            { label: "Clientes", value: fmtN(row.entregaVolumeTotalCliente) },
                            { label: "Cxs Unit.", value: fmtN(row.entregaVolumeCaixasUnitarias) },
                            { label: "Tx Ocup.", value: row.entregaVolumeTaxaOcupacao ?? "—" },
                            { label: "Cubos Ret.", value: fmtN(row.totalCubosRetornados) },
                        ].map((item) => (
                            <div key={item.label} className="bg-white rounded-lg border border-gray-100 px-2 py-1.5 text-center">
                                <p className="text-[9px] text-gray-400 uppercase">{item.label}</p>
                                <p className="text-xs font-bold text-gray-800">{item.value}</p>
                            </div>
                        ))}
                    </div>

                    {/* Greenmile */}
                    <div className="rounded-lg border border-green-100 bg-green-50 px-3 py-2 space-y-1.5">
                        <p className="text-[10px] font-semibold text-green-700 uppercase">Greenmile</p>
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                            <Detail label="Saída" value={formatLocalDateTime(row.rotaGreenmileSaidaRealizada)} />
                            <Detail label="Chegada" value={formatLocalDateTime(row.rotaGreenmileChegadaRealizada)} />
                            <Detail label="Carga" value={row.rotaGreenmileDescricaoCarga} />
                        </div>
                    </div>

                    {/* SAP */}
                    <div className="rounded-lg border border-red-100 bg-red-50 px-3 py-2 space-y-1.5">
                        <p className="text-[10px] font-semibold text-red-600 uppercase">SAP</p>
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                            <Detail label="Entrega" value={formatLocalDate(row.entregaVolumeDataEntrega)} />
                            <Detail label="Retorno" value={formatLocalDate(row.entregaVolumeDataRetorno)} />
                        </div>
                    </div>

                    {/* Botões de ação */}
                    <div className="flex gap-2 flex-wrap">
                        <button
                            onClick={(e) => { e.stopPropagation(); onClick(); }}
                            className="flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-medium rounded-lg border border-gray-200 text-gray-700 bg-white hover:bg-gray-50 transition"
                        >
                            <i className="fa fa-eye" /> Resumo completo
                        </button>
                        {row.temRetornos && (
                            <button
                                onClick={(e) => { e.stopPropagation(); onRetornos(); }}
                                className="flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-medium rounded-lg border border-blue-200 text-blue-600 bg-blue-50 hover:bg-blue-100 transition"
                            >
                                <i className="fa fa-undo" /> Retornos ({fmtN(row.totalCubosRetornados)})
                            </button>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

// ─── Sub-componentes compartilhados ──────────────────────────────────────────
function Detail({ label, value }) {
    return (
        <div>
            <span className="text-[10px] text-gray-400 uppercase block">{label}</span>
            <span className="text-xs font-medium text-gray-700">{value ?? "—"}</span>
        </div>
    );
}

const BADGE_COLORS = {
    blue: "bg-blue-100   text-blue-700",
    teal: "bg-teal-100   text-teal-700",
    green: "bg-green-100  text-green-700",
    violet: "bg-violet-100 text-violet-700",
    red: "bg-red-100    text-red-700",
    gray: "bg-gray-100   text-gray-600",
};

function Badge({ label, color = "gray" }) {
    if (!label) return null;
    return (
        <span className={`inline-block px-2.5 py-0.5 rounded-full text-[10px] font-semibold ${BADGE_COLORS[color]}`}>
            {label}
        </span>
    );
}

function LoadingState() {
    return (
        <div className="flex flex-col items-center justify-center py-16 gap-2 text-gray-400">
            <i className="fa fa-spinner fa-spin text-2xl text-teal-500" />
            <p className="text-xs">Carregando cargas...</p>
        </div>
    );
}

function EmptyState() {
    return (
        <div className="flex flex-col items-center justify-center py-16 gap-2 text-gray-400">
            <i className="fa fa-truck text-3xl opacity-20" />
            <p className="text-xs">Nenhuma carga encontrada.</p>
        </div>
    );
}